import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { saveCharacter } from '../../Store/Actions/characterActions';
import { updateUserCharacter } from '../../Store/Actions/userActions';
// saveCharacter / updateUserCharacter are plain async helpers (no thunk
// middleware) — call them with dispatch as the first arg.
import { INITIAL_HERO_INVENTORY } from '../../Classes/utils/constants';
import ClassSelection from './ClassSelection';
import AttributesAllocation from './AttributesAllocation';
import CharacterPreview from './CharacterPreview';
import FinalizeCharacter from './FinalizeCharacter';
import { log } from "../../Helpers/logger";

const CharacterCreation = () => {
  const user = useSelector(state => state.user);
  const [step, setStep] = useState(1);
  const [character, setCharacter] = useState({
    name: '',
    gender: 'male',
    classType: '',
    attributes: {
      strength: 5,
      dexterity: 5,
      intelligence: 5,
      vitality: 5,
    },
    points: 10, // Points à répartir
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const nextStep = () => setStep(prev => prev + 1);
  const prevStep = () => setStep(prev => prev - 1);

  const handleChange = (field, value) => {
    setCharacter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAttributeChange = (...args) => {
    // Si le premier argument est un objet, c'est la nouvelle version du personnage
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      setCharacter(args[0]);
    } 
    // Sinon, c'est l'ancienne signature (attribut, valeur)
    else if (args.length === 2) {
      const [attribute, value] = args;
      setCharacter(prev => ({
        ...prev,
        attributes: {
          ...prev.attributes,
          [attribute]: value
        }
      }));
    }
  };

  const { t } = useTranslation();
  const handleSubmit = async () => {
    try {
      // Transformer les attributs du personnage au format attendu
      const { attributes, classType, name, gender } = character;
      
      // Créer un objet stats avec les attributs et les valeurs par défaut de santé et mana
      const stats = {
        // Add base attributes with number conversion
        strength: Math.max(1, parseInt(attributes.strength || 10, 10)),
        intelligence: Math.max(1, parseInt(attributes.intelligence || 10, 10)),
        dexterity: Math.max(1, parseInt(attributes.dexterity || 10, 10)),
        vitality: Math.max(1, parseInt(attributes.vitality || 10, 10)),
        // Calculate health and mana based on vitality and intelligence
        health: 30 + (attributes.vitality * 2),
        maxHealth: 30 + (attributes.vitality * 2),
        mana: 10 + (attributes.intelligence * 2),
        maxMana: 10 + (attributes.intelligence * 2)
      };
      
      // Créer l'objet personnage complet avec les attributs transformés
      const characterData = {
        id: `hero_${Date.now()}`,
        userId: user.id,
        name,
        gender,
        classType: classType?.toLowerCase() || 'sworder',
        stats,
        level: 1,
        xp: 0,
        gold: 100,
        position: { mapId: 'map1', x: 5, y: 4 },
        // Starter Bourse — same elixirs as INITIAL_HERO_INVENTORY so the
        // new éveillé doesn't begin with an empty bag.
        inventory: { ...INITIAL_HERO_INVENTORY },
        skills: [],
        equipment: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      log.ui.log('Saving character data:', characterData);
      
      // Save the character to Redux and localStorage
      const savedCharacter = await saveCharacter(dispatch, characterData);
      log.ui.log('Character saved successfully:', savedCharacter);

      // updateUserCharacter rebuilds the hero and replaces user.gameData.hero;
      // pass the current user explicitly (no more getState).
      await updateUserCharacter(dispatch, savedCharacter, user);
      
      log.ui.log('User updated with character, redirecting to /game');
      
      // Force a small delay to ensure Redux state updates
      setTimeout(() => {
        navigate('/game');
      }, 100);
      
    } catch (error) {
      console.error('Error creating character:', error);
      alert(t('characterCreation.errors.creation'));
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <ClassSelection
            character={character}
            onSelectClass={(classType) => handleChange('classType', classType)}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <AttributesAllocation
            character={character}
            onAttributeChange={handleAttributeChange}
            onNext={nextStep}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <CharacterPreview
            character={character}
            onCharacterChange={setCharacter}
            onBack={prevStep}
            onNext={nextStep}
          />
        );
      case 4:
        return (
          <FinalizeCharacter
            character={character}
            onBack={prevStep}
            onSubmit={handleSubmit}
          />
        );
      default:
        return null;
    }
  };

  const STEPS = [
    { key: 1, name: t('characterCreation.steps.class') },
    { key: 2, name: t('characterCreation.steps.attributes') },
    { key: 3, name: t('characterCreation.steps.preview') },
    { key: 4, name: t('characterCreation.steps.seal') },
  ];

  const renderStepper = () => (
    <div className="okstep">
      {STEPS.map((s, idx) => (
        <React.Fragment key={s.key}>
          <div
            className="okstep-dot"
            data-on={step === s.key ? '' : undefined}
            data-done={step > s.key ? '' : undefined}
          >
            {s.key}
          </div>
          <span
            className="okstep-name"
            data-on={step === s.key ? '' : undefined}
          >
            {s.name}
          </span>
          {idx < STEPS.length - 1 && (
            <div
              className="okstep-bar"
              data-done={step > s.key ? '' : undefined}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="okframe okframe-page" data-screen-label="02 Character Creation" style={{ minHeight: 'auto', padding: '32px 0 48px' }}>
      <div className="okframe-head">
        <div className="okframe-title">{t('characterCreation.title')}</div>
        <div className="okframe-sub">{t('characterCreation.subtitle')}</div>
        <div className="okframe-rule" />
      </div>
      {renderStepper()}
      <div className="okcard" style={{ maxWidth: 1080, margin: '0 auto', minHeight: 320 }}>
        {renderStep()}
      </div>
    </div>
  );
};



export default CharacterCreation;
